/**
 * 사이트(호스트)별 파비콘 브랜딩
 *
 * 정책
 *   1. khrdc.org  → 대한혈액학회 전용 파비콘 고정 (API 조회 안 함)
 *   2. 그 외 도메인
 *      - 로그인 전 / 학회 미확정  → U2EDC 기본 파비콘
 *      - 학회 확정 후             → DB에서 해당 학회 아이콘 조회 → 있으면 교체
 *      - 아이콘이 없거나 조회 실패 → U2EDC 기본 파비콘 유지
 *
 * 정적 파비콘 파일은 public/ 아래 아래 규칙으로 배치한다.
 *   /favicon-{key}.ico
 *   /favicon-{key}.svg          (선택)
 *   /favicon-{key}-32x32.png
 *   /favicon-{key}-16x16.png
 *   /apple-touch-icon-{key}.png
 *
 * 최초 적용(Vue 로딩 전)은 index.html 인라인 스크립트가 담당한다.
 * 이 모듈은 그 이후 학회별 동적 교체를 담당한다.
 */

import { callApi } from '../api/useAPI';

// 호스트에 포함된 문자열 → 고정 브랜드 key (API 조회 없이 이 아이콘을 계속 사용)
const FIXED_BRAND_HOSTS = [{ match: 'khrdc', key: 'khrdc' }];

// 위에 걸리지 않는 모든 사이트의 기본 브랜드
export const DEFAULT_BRAND_KEY = 'u2';

// ─────────────────────────────────────────────────────────────
// 학회별 파비콘 조회 API
// TODO: 백엔드 엔드포인트 구현 후 ENABLE_REMOTE_FAVICON 을 true 로 변경
//       응답 예시: [{ "FaviconUrl": "https://.../favicon.png" }]
// ─────────────────────────────────────────────────────────────
const ENABLE_REMOTE_FAVICON = false;
const FAVICON_API = '/API/getAssociationFavicon';

/** 현재 호스트가 고정 브랜드인지 (khrdc 등) */
export function getFixedBrandKey(hostname) {
  const host = (hostname ?? window.location.hostname ?? '').toLowerCase();
  const found = FIXED_BRAND_HOSTS.find((b) => host.includes(b.match));
  return found ? found.key : null;
}

/** 브랜드 key로 정적 파비콘 경로 세트 생성 (파일명 규칙 기반) */
export function getIconSet(key) {
  return {
    ico: `/favicon-${key}.ico`,
    svg: key === DEFAULT_BRAND_KEY ? `/favicon-${key}.svg` : null,
    png32: `/favicon-${key}-32x32.png`,
    png16: `/favicon-${key}-16x16.png`,
    apple: `/apple-touch-icon-${key}.png`,
  };
}

/**
 * 문서의 파비콘 link 태그를 교체한다.
 * @param {{ico?:string, svg?:string, png32?:string, png16?:string, apple?:string}} icons
 */
export function applyFavicon(icons) {
  if (!icons) return;

  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove());

  const add = (attrs) => {
    const link = document.createElement('link');
    Object.entries(attrs).forEach(([k, v]) => {
      if (v != null) link.setAttribute(k, v);
    });
    document.head.appendChild(link);
  };

  if (icons.ico) add({ rel: 'icon', href: icons.ico, sizes: 'any' });
  if (icons.svg) add({ rel: 'icon', type: 'image/svg+xml', href: icons.svg });
  if (icons.png32) add({ rel: 'icon', type: 'image/png', sizes: '32x32', href: icons.png32 });
  if (icons.png16) add({ rel: 'icon', type: 'image/png', sizes: '16x16', href: icons.png16 });
  if (icons.apple) add({ rel: 'apple-touch-icon', sizes: '180x180', href: icons.apple });
}

/** 단일 이미지 URL 하나로 파비콘 교체 (DB에서 받은 학회 아이콘용) */
export function applyFaviconUrl(url) {
  if (!url) return;
  applyFavicon({ ico: null, svg: null, png32: url, png16: url, apple: url });
}

/** URL이 실제로 이미지로 로드되는지 확인 (깨진 링크로 교체되는 것 방지) */
function canLoadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/** U2EDC 기본 파비콘으로 되돌림 */
export function applyDefaultFavicon() {
  applyFavicon(getIconSet(DEFAULT_BRAND_KEY));
}

/**
 * 현재 호스트/학회에 맞는 파비콘을 적용한다.
 * 실패해도 예외를 던지지 않고 기본 파비콘을 유지한다.
 *
 * @param {string|number|null} associationID 로그인한 학회 ID (없으면 기본 파비콘)
 */
export async function refreshFavicon(associationID) {
  // 1. khrdc 등 고정 브랜드 호스트는 그대로 둔다
  const fixedKey = getFixedBrandKey();
  if (fixedKey) {
    applyFavicon(getIconSet(fixedKey));
    return;
  }

  // 2. 기본값(U2EDC)을 먼저 깔아둔다 — 이후 조회에 실패해도 이 상태가 유지된다
  applyDefaultFavicon();

  // 3. 학회가 정해지지 않았거나 API가 아직 준비되지 않았으면 여기서 종료
  if (!associationID || !ENABLE_REMOTE_FAVICON) return;

  // 4. DB에서 학회 아이콘 조회 → 성공 시에만 교체
  try {
    const res = await callApi(FAVICON_API, { AssociationID: Number(associationID) }, associationID);
    const url = Array.isArray(res) ? res[0]?.FaviconUrl : res?.FaviconUrl;
    if (!url) return;

    if (await canLoadImage(url)) {
      applyFaviconUrl(url);
    }
  } catch {
    // 조회 실패 시 기본 파비콘 유지 (사용자에게 노출할 오류 아님)
  }
}
